import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";

import type { SelectOption } from "@cinnamon/design-system/types";

import { API_PATH, LANGUAGE_CODE } from "@/constants/config";
import { request } from "@/helper/http";

interface OptionServer {
  // Default is en
  display_name: string;
  display_name_ja: string | null;
  code_name: string;
}

/**
 * Maps locale codes to the corresponding display name field from the backend.
 * Extend this object to support additional localized fields like display_name_ko, display_name_zh.
 */
const MODEL_DISPLAY_NAME_FIELD_BY_LOCALE = {
  [LANGUAGE_CODE.JAPANESE]: "display_name_ja" as const,
};

/**
 * Gets the localized display name for a model option.
 * Returns the localized name if available for the current locale, otherwise falls back to display_name.
 *
 * @param option - The model option containing display names
 * @param locale - The current locale code
 * @returns The appropriate display name for the locale
 */
export const getModelDisplayName = (option: OptionServer, locale: string): string => {
  const localizedField = MODEL_DISPLAY_NAME_FIELD_BY_LOCALE[locale as keyof typeof MODEL_DISPLAY_NAME_FIELD_BY_LOCALE];
  const localizedName = localizedField ? option[localizedField] : null;

  return localizedName?.trim() || option.display_name;
};
interface LLModelOption extends OptionServer {
  single: OptionServer[];
  table: OptionServer[];
}
interface MetaDataLLMServer {
  results: LLModelOption[];
}
export interface MetaDataLLM {
  llm_model_type: SelectOption;
  single_fields_llm_model_type: SelectOption[];
  table_fields_llm_model_type: SelectOption[];
}

export const getMetaDataLLM = async (): Promise<MetaDataLLMServer> => {
  const params = {
    timestamp: new Date().getTime(),
  };
  return request({
    method: "GET",
    url: `${API_PATH.REGISTRY_LLM_MODELS}`,
    params,
  });
};

/**
 * Normalizes metadata LLM data with locale-aware display names.
 *
 * @param data - The server response containing LLM model options
 * @param locale - The current locale code for selecting localized display names
 * @returns Normalized metadata with locale-appropriate labels
 */
export const normalizeMetaDataLLM = (data: MetaDataLLMServer, locale: string): MetaDataLLM[] => {
  const { results } = data;
  return results.map((option) => ({
    llm_model_type: {
      value: option.code_name,
      label: getModelDisplayName(option, locale),
    },
    single_fields_llm_model_type: option.single.map((singleOption) => ({
      value: singleOption.code_name,
      label: getModelDisplayName(singleOption, locale),
    })),
    table_fields_llm_model_type: option.table.map((tableOption) => ({
      value: tableOption.code_name,
      label: getModelDisplayName(tableOption, locale),
    })),
  }));
};

export const metadataApi = createApi({
  reducerPath: "metadataApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["metadata"],
  // 6 hours
  keepUnusedDataFor: 60 * 60 * 6,
  endpoints: (build) => ({
    getMetaDataLLM: build.query<MetaDataLLMServer, void>({
      queryFn: async () => {
        try {
          const data = await getMetaDataLLM();
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["metadata"],
    }),
  }),
});

export const { useGetMetaDataLLMQuery } = metadataApi;
